import React, { useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import LoadingOverlay from "./LoadingOverlay";
import ConfirmDialog from "./ConfirmDialog";
import { formatNumber } from "../utils/numbers";

const thCls =
  "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-r last:border-r-0";
const tdCls =
  "px-4 py-2.5 text-xs text-gray-700 border-r last:border-r-0 whitespace-nowrap";

function ApproveMaterial() {
  const { user } = useSelector((state) => state.auth);
  const [reqId, setReqId] = useState("");
  const [tempArray, setTempArray] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [uploadPending, setUploadPending] = useState(false);

  const formatDateTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  };

  const fetchScannedData = async () => {
    if (!reqId.trim()) {
      toast.warning("Please enter a Request / Group ID.");
      return;
    }
    setIsLoading(true);
    try {
      const res = await axios.get(
        `https://eps.elkomprintama.com/api/store/transaction_group/${reqId.trim()}`,
      );
      const formatted = res.data.result.map((item, idx) => {
        const isStockIn =
          item.transaction_type === "Pending Stock In" ||
          item.transaction_type === "QC Pending Stock In";
        return {
          no: idx + 1,
          product_id: item.product_id,
          product_name: item.product_name,
          temp_qty: isStockIn ? item.stock_in : item.stock_out,
          transaction_type: item.transaction_type,
          transaction_for: item.transaction_for,
          transaction_remark: item.transaction_remark,
          transaction_by: item.transaction_by,
          transaction_time: item.transaction_time,
        };
      });
      setTempArray(formatted);
      if (formatted.length === 0)
        toast.warning("No transactions found for this ID.");
    } catch {
      toast.error("Failed to fetch transaction data. Check the Request ID.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!reqId.trim()) {
      toast.warning("No Request ID entered.");
      return;
    }
    if (tempArray.length === 0) {
      toast.warning("Please check the Request ID first.");
      return;
    }
    setUploadPending(true);
    setShowConfirm(true);
  };

  const submitApproval = async () => {
    setIsLoading(true);
    try {
      const type = tempArray[0]?.transaction_type;
      const isStockIn =
        type === "Pending Stock In" || type === "QC Pending Stock In";
      const endpoint = isStockIn
        ? "https://eps.elkomprintama.com/api/store/stocks/approve_stockin"
        : "https://eps.elkomprintama.com/api/store/stocks/approve";

      await axios.put(endpoint, {
        groupId: reqId.trim(),
        approvedBy: user.username,
      });
      toast.success("Request approved successfully!");
      setReqId("");
      setTempArray([]);
    } catch (err) {
      toast.error(
        "Approval failed: " + (err?.response?.data?.msg || err.message),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const statusBadge = (type) => {
    if (type === "CANCELLED") return "bg-red-100 text-red-700";
    if (type === "Stock Out" || type === "Approved")
      return "bg-green-100 text-green-700";
    return "bg-yellow-100 text-yellow-700";
  };

  const transactionTypeLabel = tempArray[0]?.transaction_type;
  const isStockIn =
    transactionTypeLabel === "Pending Stock In" ||
    transactionTypeLabel === "QC Pending Stock In";

  return (
    <div className="flex flex-col" style={{ maxHeight: "calc(100vh - 80px)" }}>
      {isLoading && <LoadingOverlay message="Processing…" />}

      {/* Header */}
      <div className="w-full rounded-lg bg-blue-700 mb-4 flex-shrink-0">
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          Approve Material
        </label>
      </div>

      {/* Body card */}
      <form
        onSubmit={handleSubmit}
        className="flex-1 min-h-0 flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
      >
        {/* Input section */}
        <div className="flex-shrink-0 px-6 py-5 border-b border-gray-100 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Scan / Enter Request ID
            </label>
            <div className="flex gap-3">
              <input
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400"
                name="reqid" type="text"
                placeholder="Paste or scan group ID here"
                value={reqId}
                onChange={(e) => {
                  setReqId(e.target.value);
                  setTempArray([]);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    fetchScannedData();
                  }
                }}
              />
              <button
                type="button"
                onClick={fetchScannedData}
                className="px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-gray-700 hover:bg-gray-800 transition whitespace-nowrap"
              >
                Check
              </button>
            </div>
            <p className="text-xs text-gray-400 mt-1.5">
              Press Enter or click Check to load the request items.
            </p>
          </div>

          {/* Batch summary */}
          {tempArray.length > 0 && (
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-blue-50 border border-blue-200">
              <div className="text-xs text-blue-700 font-semibold">
                {tempArray.length} item{tempArray.length !== 1 ? "s" : ""}{" "}
                loaded
              </div>
              <div className="w-px h-4 bg-blue-200" />
              <div className="text-xs text-blue-600">
                Type: <span className="font-bold">{transactionTypeLabel}</span>
              </div>
              <div className="w-px h-4 bg-blue-200" />
              <div className="text-xs text-blue-600">
                By:{" "}
                <span className="font-bold">
                  {tempArray[0]?.transaction_by}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="flex-1 min-h-0 overflow-auto">
          {tempArray.length === 0 ? (
            <div className="h-full flex items-center justify-center text-gray-400 italic text-sm">
              Enter a Request ID and click Check to preview items
            </div>
          ) : (
            <table className="w-full text-xs border-collapse">
              <thead className="sticky top-0 bg-gray-700 text-white uppercase">
                <tr>
                  <th className={thCls}>No</th>
                  <th className={thCls}>Product Name</th>
                  <th className={thCls}>Request Time</th>
                  <th className={thCls}>QTY</th>
                  <th className={thCls}>Remark</th>
                  <th className={thCls}>
                    {isStockIn ? "Supplier" : "Customer"}
                  </th>
                  <th className={thCls}>Input By</th>
                  <th className={thCls}>Status</th>
                </tr>
              </thead>
              <tbody>
                {tempArray.map((data, i) => (
                  <tr
                    key={data.no}
                    className={`border-b hover:bg-blue-50 ${i % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                  >
                    <td className={`${tdCls} text-gray-400`}>{data.no}</td>
                    <td className={`${tdCls} font-medium text-gray-800`}>
                      {data.product_name}
                    </td>
                    <td className={tdCls}>
                      {formatDateTime(data.transaction_time)}
                    </td>
                    <td className="px-4 py-2.5 border-r whitespace-nowrap">
                      <span className="font-bold text-blue-700">
                        {formatNumber(data.temp_qty)}
                      </span>
                    </td>
                    <td className={tdCls}>{data.transaction_remark || "—"}</td>
                    <td className={tdCls}>{data.transaction_for || "—"}</td>
                    <td className={tdCls}>{data.transaction_by}</td>
                    <td className="px-4 py-2.5 border-r whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${statusBadge(data.transaction_type)}`}
                      >
                        {data.transaction_type}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Approve footer */}
        {tempArray.length > 0 && (
          <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100">
            <button
              type="submit"
              className="w-full py-3 rounded-xl text-sm font-bold text-white bg-blue-700 hover:bg-blue-800 transition"
            >
              Approve {tempArray.length} Item{tempArray.length !== 1 ? "s" : ""}
            </button>
          </div>
        )}
      </form>

      {showConfirm && uploadPending && (
        <ConfirmDialog
          message="APPROVE this material request? Please verify the items are correct before confirming."
          onConfirm={() => {
            setShowConfirm(false);
            submitApproval();
            setUploadPending(false);
          }}
          onCancel={() => {
            setShowConfirm(false);
            setUploadPending(false);
          }}
        />
      )}
    </div>
  );
}

export default ApproveMaterial;
